import React, {useContext, useEffect, useState} from "react";
import {
    Box,
    Stack,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Paper,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    List,
    ListItemButton,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {IconExit} from "../../../assets/icons/IconExit";
import {MobileUnavailable} from "../../../components/MobileUnavailable";
import {
    IconCopy,
    IconTrash,
    IconChevronRight,
    IconPlus,
    IconMinus,
    IconPlayerPlay,
    IconMaximize,
    IconSearch,
    IconSparkles,
    IconCornerDownLeft
} from "@tabler/icons-react";
import {Context as AppContext} from "../../../context/AppContext";
import ReactFlow, {
    MiniMap,
    Panel,
    useEdgesState,
    useNodesState,
    useReactFlow
} from "reactflow";

import 'reactflow/dist/style.css';
import BlockNode from "../../../nodes/BlockNode";
import {v4 as uuidv4} from 'uuid';
import "../../../assets/css/overview.css"
import StartNode from "../../../nodes/StartNode";
import IntentNode from "../../../nodes/IntentNode";
import {NodeMenu} from "./NodeMenu"
import {
    getRoutes, mapNode, mapStartFormToNode,
} from "../../../utils/nodeMapper";
import {toast} from "react-toastify";
import {SideMenu} from "./side-menu/SideMenu";
import {NodeEntity, NodeType, ScopeType, TargetType} from "../../../model/ModelData";
import CustomNode from "../../../nodes/CustomNode";
import EndNode from "../../../nodes/EndNode";
import FormNode from "../../../nodes/FormNode";
import {EditNodeMenuWrapper} from "./edit-menu/EditNodeMenuWrapper";
import {useLLMNodes} from "./edit-menu/settings/LLMSettings";
import useConfirm from "../../../hooks/useConfirm";
import CustomEdgeStartEnd from "../../../nodes/edges/CustomEdgeStartEnd";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import TransferCallNode from "../../../nodes/TransferCallNode";


const nodeTypes = {
    block: BlockNode,
    start: StartNode,
    intent: IntentNode,
    custom: CustomNode,
    end: EndNode,
    transfer_call: TransferCallNode,
    // flow: FlowNode,
    form: FormNode
};

const edgeTypes = {
    'start-end': CustomEdgeStartEnd,
};

const minimapStyle = {
    height: 120,
};

export const Project = ({project, openChat}) => {
    const {updateProject} = useContext(AppContext)
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedFlow, setSelectedFlow] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [useLLM, setUseLLM] = useState(false);
    const [nodeSearch, setNodeSearch] = useState("");
    const {
        setCenter,
        project: reactFlowProject,
        zoomIn,
        zoomOut,
        fitView
    } = useReactFlow();
    const [contextMenu, setContextMenu] = useState(null);
    const confirmEdgeDelete = useConfirm(
        'Are you sure you want to delete this route?',
    );
    const confirmNodeDelete = useConfirm(
        'Are you sure you want to delete this node?',
    );
    const [searchMode, setSearchMode] = useState("search");
    const [otherFlowsAnchor, setOtherFlowsAnchor] = useState(null);
    const otherFlowsOpen = Boolean(otherFlowsAnchor);
    const otherFlows = (project?.flows || []).filter(f => f.id !== selectedFlow?.id);
    const [flowNodes, setFlowNodes] = useState([]);
    const {flowId} = useParams();
    const navigate = useNavigate();
    let [searchParams, setSearchParams] = useSearchParams();
    const [aiLoading, setAiLoading] = useState(false);

    const runAiSearch = async () => {
        const query = nodeSearch.trim();
        if (!query || aiLoading) return;

        setAiLoading(true);
        try {
            console.log("AI Search Mock")
            // TODO: implement real function call
        } catch (error) {
            console.error("AI search failed:", error);
        } finally {
            setAiLoading(false);
        }
    };

    useEffect(() => {
        if (project && flowNodes && flowNodes?.length > 0) {
            const nodeItems = [];
            let edgeItems = [];
            for (const flowNode of flowNodes) {
                let node = null;
                if (flowNode?.data.form_id) {
                    if (flowNode.type === NodeType.START_FORM) {
                        const {newNode, edgeItems: newEdges} = mapStartFormToNode({node: flowNode, nodes: flowNodes});
                        node = newNode;
                        edgeItems = [...edgeItems, ...newEdges];
                    }
                } else {
                    const {newNode, edgeItems: newEdges} = mapNode({
                        node: flowNode,
                        nodes: flowNodes,
                        intents: project?.intents || []
                    });
                    node = newNode;
                    edgeItems = [...edgeItems, ...newEdges];
                }

                if (node) {
                    nodeItems.push(node)
                }
            }
            setNodes(nodeItems)
            setEdges(edgeItems)
        }
    }, [flowNodes, project?.intents])

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const onConnect = async (params) => {
        if (openChat) {
            return;
        }
        try {
            const sourceNode = flowNodes?.find(n => n?.id === params.source);
            const routes = getRoutes(sourceNode?.routes);
            let route = routes?.find(r => r.id === params.sourceHandle);
            const target = flowNodes.find(n => n.id === params.target);
            if (sourceNode && route && target) {
                if (route?.target === undefined) {
                    route = {
                        id: uuidv4(),
                        target: null,
                        name: null,
                        type: params?.sourceHandle,
                        backstack: false,
                        data: {
                            greet: "",
                            params: []
                        }
                    }
                }
                route.target = target.id;
                route.name = target.name;
                for (const staticKey of Object.keys(sourceNode.routes.static)) {
                    if (staticKey === route.type) {
                        sourceNode.routes.static[staticKey] = route;
                    }
                }
                for (const dynamic in sourceNode.routes.dynamic) {
                    const dynamicRoute = sourceNode?.routes?.dynamic[dynamic];

                    if (dynamicRoute && dynamicRoute?.id === route?.id) {
                        sourceNode.routes.dynamic[dynamic] = route;
                    }
                }
                await onUpdateNode(sourceNode);
            }
        } catch (error) {
            toast.error("Failed to connect")
        }
    };

    useEffect(() => {
        if (project && project?.flows?.length) {
            let newFlow = project?.flows[0];
            if (flowId) {
                newFlow = project?.flows?.find(i => i?.id === flowId);
            } else if (selectedFlow) {
                newFlow = project.flows.find(i => i.id === selectedFlow.id);
            } else if ((flowId !== undefined && flowId !== null) && newFlow?.id) {
                navigate(`/project/${project?.id}/flow/${newFlow?.id}`);
                return;
            }
            const prevStartNode = flowNodes?.find(i => i.type === NodeType.START);
            const currentStartNode = newFlow?.nodes?.find(i => i.type === NodeType.START);
            if (prevStartNode?.id !== currentStartNode?.id) {
                const center = {x: currentStartNode?.coords?.x || 0, y: currentStartNode?.coords?.y || 0, zoom: 1};
                setCenter(center.x + 100, center.y, {zoom: 0.9, duration: 700});
            }

            setFlowNodes(newFlow?.nodes);
            setSelectedFlow(newFlow);

        }
    }, [project, flowId])

    // Escape clears the query while the search field has focus.
    useEffect(() => {
        if (!nodeSearch) return;
        const onKey = (event) => {
            if (event.key === "Escape" && event.target?.tagName === "TEXTAREA") {
                event.preventDefault();
                setNodeSearch("");
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [nodeSearch]);

    useEffect(() => {
        if (selectedNode && openChat) {
            const node = nodes?.find(i => i.id === selectedNode?.id);
            if (node) {
                const center = {x: selectedNode?.coords?.x || 0, y: selectedNode?.coords?.y || 0, zoom: 1};
                setCenter(center.x + 100, center.y, {zoom: 0.9, duration: 700});
                setNodes(prev => {
                    return prev.map(n => {
                        if ((n.id === selectedNode.id)
                            || (selectedNode?.data?.form_id
                                && n.data?.node?.data?.form_id
                                && n.data?.node?.data?.form_id === selectedNode
                                && n.data?.node?.data?.type === NodeType.START_FORM
                            )) {
                            return {...n, selected: true};
                        }
                        return {...n, selected: false};
                    })
                });
            }
        }
    }, [selectedNode])

    const onNodeContextMenu = async (event, node) => {
        event.preventDefault();
        if (openChat) {
            return;
        }
        setContextMenu({node, top: event.clientY, left: event.clientX});
    }

    const closeContextMenu = () => {
        setContextMenu(null);
        setOtherFlowsAnchor(null);
    };

    const handleCopyToFlow = async (targetFlowId) => {
        const id = contextMenu?.node?.id;
        closeContextMenu();
        if (id) await duplicateNode(id, targetFlowId);
    };


    const onDeleteNode = async (id) => {
        closeContextMenu();
        if (!id) return;
        const node = flowNodes?.find(i => i?.id === id);
        const response = await confirmNodeDelete({message: node?.name || ""});
        if (response) await removeNode(id);
    }

    useEffect(() => {
        if (!selectedNode || openChat) return;
        const onKeyDown = (event) => {
            const target = event.target;
            if (target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName)) {
                return;
            }
            if (event.key === "Delete") {
                event.preventDefault();
                onDeleteNode(selectedNode?.id);
            } else if (event.key === "Escape") {
                event.preventDefault();
                setSelectedNode(null);
                setSearchParams({});
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [selectedNode, openChat, flowNodes]);

    const addNode = async (type) => {
        if (type === NodeType.START_FORM) {
            await addFormNode();
            return;
        }
        const screenCenter = {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        };

        const center = reactFlowProject(screenCenter);

        const newNode = {
            ...NodeEntity,
            id: uuidv4(),
            name: `${type?.toLowerCase()?.replaceAll("_", " ")} [${nodes?.length}]`,
            type: type, // NodeType
            coords: {x: (center?.x || 450) - 300, y: center?.y || 150},
            parameters: [],
            data: {
                greet: [],
                max_ttl: project?.settings?.max_ttl || 4
            },
            routes: {
                static: {
                    next: null,
                    no_match: null,
                    fail: null,
                },
                dynamic: []
            }
        };
        if (type === NodeType.QA || type === NodeType.RECOMMENDATION) {
            newNode.data.qa_method = project?.settings?.qa_method || "";
            newNode.data.qa_api = project?.settings?.qa_api || "";
            newNode.data.please_wait_messages = [];
            newNode.data.persona = "";
            newNode.data.slots = [];
            newNode.data.paraphrazer = true;
            newNode.data.lemmatizer = true;
            newNode.data.conversation_buffer_window = 4;
        } else if (type === NodeType.HOOK) {
            newNode.data.api_url = "";
            newNode.data.api_method = "GET";
            newNode.data.target = TargetType.FLOW;
            newNode.data.params = [{id: uuidv4(), source: null, scope: ScopeType.FLOW}];
        } else if (type === NodeType.TEACHING) {
            newNode.data.topic_id = null;
            newNode.data.slot_id = null;
            newNode.data.use_llm = true;
            newNode.data.teach_kb_extractor_hook = "";
            newNode.data.qa_method = "GET";
            newNode.data.kb_add_hook = "";
            newNode.data.extractor_parser = {};
        }
        if (type === NodeType.RECOMMENDATION || type === NodeType.TEACHING || type === NodeType.JAVASCRIPT) {
            newNode.data.parser = `
function parser(result) {
  // Write your code here
}
`;
        }
        if (type === NodeType.AGENT || type === NodeType.CHATBOT) {
            newNode.data.learning = {
                config_version: "1.0",
                enabled: false,
            };
        }
        if (type !== NodeType.QUERY && type !== NodeType.END && type !== NodeType.END_FLOW) {
            newNode.routes.static.next = {
                id: uuidv4(),
                target: " ",
                name: null,
                type: "next",
                backstack: false,
                data: {
                    greet: "",
                    params: [],
                }
            }
        }
        await onUpdateNodes([...flowNodes, newNode]);
    }

    const addFormNode = async (type) => {
        const form_id = uuidv4();

        const endNode = {
            ...NodeEntity,
            id: uuidv4(),
            name: "End node",
            type: NodeType.END_FORM, // NodeType
            coords: {x: 0, y: 0},
            parameters: [],
            data: {
                greet: [],
                form_id: form_id,
            },
            routes: {
                static: {
                    next: null,
                    no_match: null,
                    fail: null,
                },
                dynamic: []
            }
        };
        const startNode = {
            ...NodeEntity,
            id: uuidv4(),
            name: "Start node",
            type: NodeType.START_FORM, // NodeType
            coords: {x: 0, y: 0},
            parameters: [],
            data: {
                greet: [],
                form_id: form_id,
            },
            routes: {
                static: {
                    next: {
                        id: uuidv4(),
                        target: endNode.id,
                        name: null,
                        type: "next",
                        backstack: false,
                        data: {
                            greet: ""
                        }
                    },
                    no_match: null,
                    fail: null,
                },
                dynamic: []
            }
        };

        await onUpdateNodes([...flowNodes, startNode, endNode])
    }

    const removeNode = async (id) => {
        const node = flowNodes?.find(i => i?.id === id);
        let newNodes = flowNodes?.filter(i => i?.id !== id) || [];
        newNodes = newNodes.map(i => {
            for (const key of Object.keys(i.routes.static)) {
                if (i.routes.static[key]?.target === id) {
                    i.routes.static[key].target = null;
                }
            }
            if (i.routes.dynamic && i.routes.dynamic.length > 0) {
                i.routes.dynamic = i.routes.dynamic.filter(route => route.target !== id);
            }
            return i;
        })
        if (node?.data?.form_id && node?.type === NodeType?.START_FORM) {
            newNodes = newNodes?.filter(i => i?.data?.form_id !== node?.data?.form_id)
        }
        await onUpdateNodes(newNodes)
    }

    const duplicateNode = async (id, targetFlowId = selectedFlow?.id) => {
        const node = flowNodes?.find(i => i?.id === id);
        if (!node) return;

        const copyNode = structuredClone(node);
        copyNode.id = uuidv4();
        copyNode.name = `${node.name} copy`;
        copyNode.coords = {x: node.coords.x + 40, y: node.coords.y + 40};

        if (targetFlowId === selectedFlow?.id) {
            copyNode.routes.static.next = null;
            await onUpdateNodes([...flowNodes, copyNode]);
            return;
        }

        copyNode.routes = {
            static: {next: null, no_match: null, fail: null},
            dynamic: []
        };
        copyNode.coords = {x: 10, y: 10};

        const newProject = {...project};
        newProject.flows = project.flows.map(flow =>
            flow.id === targetFlowId
                ? {...flow, nodes: [...(flow.nodes || []), copyNode]}
                : flow
        );
        await updateProject(newProject);
        toast.success("Node copied to flow");
        navigate(`/project/${project?.id}/flow/${targetFlowId}`);
    };

    const removeFormNode = async (form_id) => {
        const startNode = flowNodes?.find(i => i?.data?.form_id === form_id && i?.type === NodeType.START_FORM);
        const newNodes = flowNodes?.filter(i => i?.data?.form_id !== form_id)
            ?.map(i => {
                for (const key of Object.keys(i.routes.static)) {
                    if (i.routes.static[key]?.target === startNode?.id) {
                        i.routes.static[key].target = null;
                    }
                }
                if (i.routes.dynamic && i.routes.dynamic.length > 0) {
                    i.routes.dynamic = i.routes.dynamic.filter(route => route.target !== startNode?.id);
                }
                return i;
            });

        await onUpdateNodes(newNodes);
    }

    const onDeleteEdge = async (_, edge) => {
        const {source, sourceHandle} = edge;
        const sourceNode = flowNodes?.find(n => n?.id === source);
        if (!sourceNode) {
            return;
        }
        const response = await confirmEdgeDelete();
        if (!response) return;

        const node = {...sourceNode}
        for (const key of Object.keys(node?.routes.static)) {
            if (node?.routes?.static[key]?.id === sourceHandle) {
                node.routes.static[key] = null;
                break;
            }
        }
        node.routes.dynamic = node?.routes?.dynamic?.filter(route => route?.id !== sourceHandle);
        const newNodes = flowNodes.map(item => {
            if (item?.id === node?.id) {
                return node;
            }
            return item;
        })
        await onUpdateNodes(newNodes);
    }

    const onUpdateNode = async (node) => {
        const newNodes = flowNodes?.map(n => {
            if (n?.id === node?.id) {
                return node;
            }
            return n;
        });
        await onUpdateNodes(newNodes);
    }

    const onUpdateNodes = async (newNodes) => {
        try {
            const newProject = {...project};
            const newFlow = {...selectedFlow, nodes: newNodes};
            const newFlows = newProject?.flows?.map(flow => {
                if (flow.id === selectedFlow?.id) {
                    return newFlow;
                }
                return flow;
            })
            newProject.flows = newFlows;
            await updateProject(newProject);
        } catch (err) {
            toast.error(err.message);
        }
    }

    if (isMobile) {
        return <MobileUnavailable/>;
    }

    const centerOnNode = (nodeTypes, label) => {
        const rawNode = flowNodes.find((node) =>
            nodeTypes.includes(node?.type)
        );

        if (!rawNode) {
            toast.info(`${label} node was not found`);
            return;
        }

        const targetNode = nodes.find((node) => node.id === rawNode.id);

        if (!targetNode) {
            toast.info(`${label} node is not available`);
            return;
        }

        const nodeWidth = targetNode.width || 0;
        const nodeHeight = targetNode.height || 0;

        setCenter(
            targetNode.position.x + nodeWidth / 2,
            targetNode.position.y + nodeHeight / 2,
            {
                zoom: 1,
                duration: 500
            }
        );
    };

    const goToStartNode = (event) => {
        event.stopPropagation();

        centerOnNode(
            [NodeType.START, NodeType.START_FORM],
            "Start"
        );
    };

    const goToEndNode = (event) => {
        event.stopPropagation();

        centerOnNode(
            [NodeType.END, NodeType.END_FORM],
            "End"
        );
    };

    const handleZoomIn = (event) => {
        event.stopPropagation();
        zoomIn({duration: 200});
    };

    const handleZoomOut = (event) => {
        event.stopPropagation();
        zoomOut({duration: 200});
    };

    const handleFitView = (event) => {
        event.stopPropagation();

        fitView({
            padding: 0.2,
            duration: 500
        });
    };

    // greet is an array in the node model, so take the first line for the label.
    const nodeLabel = (node) => {
        const greet = node?.data?.greet;
        return node?.data?.name
            || (Array.isArray(greet) ? greet[0] : greet)
            || node?.type;
    };

    const searchResults = searchMode === "search" && nodeSearch.trim()
        ? nodes
            .filter((node) => {
                const greet = node?.data?.greet;
                const searchText = [
                    node?.data?.name,
                    Array.isArray(greet) ? greet.join(" ") : greet,
                    node?.data?.node?.type
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                return searchText.includes(nodeSearch.toLowerCase());
            })
            .slice(0, 8)
        : [];

    const focusSearchResult = (node) => {
        const nodeWidth = node.width || 0;
        const nodeHeight = node.height || 0;

        setCenter(
            node.position.x + nodeWidth / 2,
            node.position.y + nodeHeight / 2,
            {
                zoom: 1,
                duration: 500
            }
        );

        setSelectedNode(flowNodes?.find((n) => n?.id === node.id) || null);
        setNodeSearch("");
    };


    return (
        <>
            <Stack direction={"row"} sx={{position: "relative", flex: 1, maxHeight: '100%'}}>
                {project?.settings?.mode === "production" &&
                    <Stack alignItems={"center"} justifyContent={"center"} id={"production"}
                           sx={{position: "absolute", inset: 0, bgcolor: "#00000033", zIndex: 1000}}>
                        <Typography color={"#fefefe"} fontSize={18} fontWeight={600} sx={{
                            backdropFilter: "blur(4px)",
                            bgcolor: "#00000011",
                            py: 0.5,
                            px: 2,
                            borderRadius: 1
                        }}>
                            Editing in production mode is disabled
                        </Typography>
                    </Stack>
                }
                {
                    !openChat &&
                    <SideMenu
                        project={project}
                        flows={project?.flows}
                        selectedFlow={selectedFlow}
                        onSelectFlow={(flow) => {
                            navigate(`/project/${project?.id}/flow/${flow?.id}`)
                            setSelectedNode(null);
                        }}
                    />
                }
                <Box sx={{flex: 1, width: "100%", position: "relative", bgcolor: "transparent"}}>
                    <NodeMenu
                        openChat={openChat}
                        addNode={addNode}
                        useLLM={useLLM}
                        onToggleLLM={() => setUseLLM(!useLLM)}
                        updatePosition={(id) => {
                            let nodes = [...flowNodes];

                            let node = nodes.find(n => n?.id === id);
                            if (!node) {
                                for (const flowItem of project?.flows) {
                                    const index = flowItem?.nodes?.findIndex(i => i?.id === id);
                                    if (isFinite(index) && index >= 0) {
                                        setSelectedFlow(flowItem);
                                        setFlowNodes(flowItem?.nodes || [])
                                        node = flowItem.nodes[index];
                                    }
                                }
                            }
                            if (node?.data?.form_id && selectedNode?.data?.form_id === node?.data?.form_id) {
                                return;
                            } else {
                                setSelectedNode(node || null);
                            }
                        }}
                    />
                    <ReactFlow
                        style={{
                            background: openChat ? "#F9F9F9" : "#FAFAFA",
                            borderTopRightRadius: 0,
                            borderTopLeftRadius: 0
                        }}

                        onNodeContextMenu={onNodeContextMenu}
                        deleteKeyCode={null}
                        edgeTypes={edgeTypes}
                        className={`${openChat ? "darkness" : ""} ${useLLM ? "magic-cursor" : ""}`}
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onEdgeClick={(e, edge) => {
                            var node = flowNodes?.find(i => i?.id === edge.source);
                            if (node?.data?.flow_id && node?.type === NodeType.END_FORM) {
                                node = flowNodes?.find(i => i?.data?.flow_id === node?.data?.flow_id && node?.type === NodeType.START_FORM);
                            }
                            if (node) {
                                setSelectedNode(node);

                                setSearchParams({
                                    node: edge.source,
                                    route: edge.sourceHandle
                                })
                            }
                        }}
                        onEdgeDoubleClick={onDeleteEdge}
                        defaultViewport={{x: 0, y: 0, zoom: 0.7}}
                        onPaneClick={() => {
                            setSelectedNode(null);
                            setSearchParams({})
                        }}
                        onNodeClick={(event, clickedNode) => {
                            const id = clickedNode.id;
                            const node = flowNodes.find(n => n?.id === id);
                            if (node?.data?.form_id && selectedNode?.data?.form_id === node?.data?.form_id) {
                                return;
                            }
                            if (useLLM) {
                                if (useLLMNodes.includes(node?.type)) {
                                    onUpdateNode({...node, data: {...node.data, use_llm: !node.data.use_llm}})
                                } else {
                                    toast.info("This node cant use LLM");
                                }
                            } else {
                                setSelectedNode(node || null);
                            }
                        }}
                        onNodeDragStop={async (event, draggedNode) => {
                            const id = draggedNode?.id;
                            if (draggedNode.position.x === draggedNode.data.node.coords?.x &&
                                draggedNode.position.y === draggedNode.data.node.coords?.y) {
                                return;
                            }
                            const node = flowNodes.find(n => n?.id === id);
                            setSelectedNode(node);
                            const newNodes = flowNodes.map(item => {
                                if (item?.id === id) {
                                    return {...item, coords: {x: draggedNode.position.x, y: draggedNode.position.y}}
                                }
                                return item;
                            })
                            await onUpdateNodes(newNodes);
                        }}
                        attributionPosition="top-right"
                        nodeTypes={nodeTypes}
                        fitView
                    >
                        <Panel position="top-center" style={{top: 12, margin: 0, zIndex: 10}}>
                            <Box
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => event.stopPropagation()}
                                sx={{
                                    position: "relative",
                                    width: 280
                                }}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={1}
                                    maxRows={searchMode === "ai" ? 5 : 2}
                                    value={nodeSearch}
                                    onChange={(event) => setNodeSearch(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (searchMode === "ai" && event.key === "Enter" && !event.shiftKey) {
                                            event.preventDefault();
                                            if (nodeSearch.trim()) runAiSearch();
                                        }
                                    }}
                                    placeholder={
                                        searchMode === "search"
                                            ? "Search nodes..."
                                            : "Describe what you're looking for..."
                                    }
                                    autoComplete="off"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Tooltip
                                                    title={searchMode === "search" ? "Switch to AI ✨" : "Switch to node search 🔍"}>
                                                    <IconButton
                                                        size="small"
                                                        onMouseDown={(event) => event.preventDefault()}
                                                        onClick={() => setSearchMode(searchMode === "search" ? "ai" : "search")}
                                                        sx={{
                                                            color: searchMode === "ai" ? "primary.main" : "text.secondary",
                                                            p: 0.5,
                                                            "&:hover": {backgroundColor: "action.hover"}
                                                        }}
                                                    >
                                                        {searchMode === "search" ? <IconSearch size={18}/> :
                                                            <IconSparkles size={18}/>}
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input": {fontSize: 13},
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "background.paper",
                                            borderRadius: 3,
                                            px: 1,
                                            py: 0.75,
                                            pb: (searchMode === "ai" && nodeSearch.trim()) ? 3.5 : 0.75,
                                            border: "none",
                                            boxShadow: "0 1px 3px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.06)",
                                            transition: "height 120ms ease, box-shadow 160ms ease",
                                            "& fieldset": {border: "none"},
                                            "&:hover fieldset": {border: "none"},
                                            "&.Mui-focused fieldset": {border: "none"},
                                            "&.Mui-focused": {
                                                boxShadow: "0 1px 3px rgba(15,23,42,0.08), 0 0 0 1px rgba(25,118,210,0.4), 0 0 0 4px rgba(25,118,210,0.1)"
                                            }
                                        }
                                    }}
                                />

                                {searchMode === "ai" && nodeSearch.trim() && (
                                    <Tooltip title="Run AI search">
                                        <span style={{position: "absolute", bottom: 6, right: 8}}>
                                            <IconButton
                                                size="small"
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={runAiSearch}
                                                disabled={aiLoading}
                                                sx={{
                                                    color: "primary.main",
                                                    p: 0.5,
                                                    "&:hover": {backgroundColor: "action.hover"}
                                                }}
                                            >
                                                <IconCornerDownLeft size={16}/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}

                                {nodeSearch.trim() && (
                                    <Paper
                                        elevation={0}
                                        onMouseDown={(event) => event.preventDefault()}
                                        sx={{
                                            mt: 0.5,
                                            maxHeight: 280,
                                            overflowY: "auto",
                                            borderRadius: 2,
                                            boxShadow: "0 4px 12px rgba(15,23,42,0.1), 0 0 0 1px rgba(15,23,42,0.06)"
                                        }}
                                    >
                                        {searchMode === "ai" ? (
                                            <Box sx={{p: 1.5}}>
                                                <Typography variant="body2" color="text.secondary"
                                                            sx={{fontSize: 12}}>
                                                    {aiLoading ? "Thinking..." : "Press Enter to ask"}
                                                </Typography>
                                            </Box>
                                        ) : searchResults.length > 0 ? (
                                            <List dense disablePadding>
                                                {searchResults.map((node) => (
                                                    <ListItemButton
                                                        key={node.id}
                                                        onClick={() => focusSearchResult(node)}
                                                    >
                                                        <ListItemText
                                                            primary={nodeLabel(node)}
                                                            secondary={node?.data?.node?.type}
                                                            primaryTypographyProps={{
                                                                fontSize: 13,
                                                                noWrap: true
                                                            }}
                                                            secondaryTypographyProps={{fontSize: 11}}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        ) : (
                                            <Box sx={{p: 1.5}}>
                                                <Typography variant="body2" color="text.secondary"
                                                            sx={{fontSize: 12}}>
                                                    No nodes found
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                )}
                            </Box>
                        </Panel>
                        <Panel position="bottom-right">
                            <Paper
                                elevation={4}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                    p: 0.75,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper"
                                }}
                            >
                                <Tooltip title="Zoom in" placement="left">
                                    <IconButton
                                        size="small"
                                        aria-label="Zoom in"
                                        onClick={handleZoomIn}
                                    >
                                        <IconPlus size={19}/>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Zoom out" placement="left">
                                    <IconButton
                                        size="small"
                                        aria-label="Zoom out"
                                        onClick={handleZoomOut}
                                    >
                                        <IconMinus size={19}/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Fit entire flow" placement="left">
                                    <IconButton
                                        size="small"
                                        aria-label="Fit entire flow"
                                        onClick={handleFitView}
                                    >
                                        <IconMaximize size={18}/>
                                    </IconButton>
                                </Tooltip>

                                <Divider sx={{my: 0.5}}/>

                                <Tooltip title="Go to start node" placement="left">
                                    <IconButton
                                        size="small"
                                        aria-label="Go to start node"
                                        onClick={goToStartNode}
                                    >
                                        <IconPlayerPlay size={18}/>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Go to end node" placement="left">
                                    <IconButton
                                        size="small"
                                        aria-label="Go to end node"
                                        onClick={goToEndNode}
                                    >
                                        <IconExit size={18} color={"#252555"}/>
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        </Panel>
                        <MiniMap
                            position="bottom-left"
                            style={minimapStyle}
                            zoomable
                            pannable
                        />
                    </ReactFlow>
                </Box>
                {
                    selectedNode && openChat === false &&
                    <EditNodeMenuWrapper
                        open={!!selectedNode}
                        selectedNode={selectedNode}
                        flow={selectedFlow}
                        project={project}
                        nodes={flowNodes}
                        onClose={() => setSelectedNode(null)}
                        onUpdateNodes={onUpdateNodes}
                        onDuplicate={duplicateNode}
                        onUpdate={async (node) => {
                            if (node !== selectedNode) {
                                await onUpdateNode(node);
                                setSelectedNode(node);
                            }
                        }}
                        onRemoveForm={removeFormNode}
                        onRemove={removeNode}
                    />
                }

                <Menu
                    open={!!contextMenu}
                    onClose={closeContextMenu}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu ? {top: contextMenu.top, left: contextMenu.left} : undefined}
                    slotProps={{paper: {sx: {minWidth: 150, maxHeight: 320}}}}
                    MenuListProps={{dense: true, sx: {py: 0.5}}}
                >
                    <Typography sx={{px: 2, py: 0.5, fontSize: "0.7rem", color: "text.secondary"}}>Copy to</Typography>

                    <MenuItem onClick={() => handleCopyToFlow(selectedFlow?.id)}
                              sx={{minHeight: 0, py: 0.5, fontSize: "0.78rem"}}>
                        <ListItemIcon sx={{minWidth: "26px !important"}}><IconCopy size={15}/></ListItemIcon>
                        <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>This flow</ListItemText>
                    </MenuItem>

                    {otherFlows.length > 0 && (
                        <MenuItem onClick={(e) => setOtherFlowsAnchor(e.currentTarget)}
                                  sx={{minHeight: 0, py: 0.5, fontSize: "0.78rem", justifyContent: "space-between"}}>
                            <Box sx={{display: "flex", alignItems: "center"}}>
                                <ListItemIcon sx={{minWidth: "26px !important"}}><IconCopy size={15}/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>Other flows</ListItemText>
                            </Box>
                            <IconChevronRight size={14}/>
                        </MenuItem>
                    )}
                    <Menu
                        anchorEl={otherFlowsAnchor}
                        open={otherFlowsOpen}
                        onClose={() => setOtherFlowsAnchor(null)}
                        anchorOrigin={{vertical: "top", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "left"}}
                    >
                        {otherFlows.map(f => (
                            <MenuItem key={f.id}
                                      onClick={() => {
                                          handleCopyToFlow(f.id);
                                          setOtherFlowsAnchor(null);
                                      }}
                                      sx={{minHeight: 0, py: 0.5, fontSize: "0.78rem"}}>
                                <ListItemIcon sx={{minWidth: "26px !important"}}><IconCopy size={15}/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>{f.name}</ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>

                    <Divider sx={{my: 0.5}}/>

                    <MenuItem onClick={() => onDeleteNode(contextMenu?.node?.id)}
                              sx={{minHeight: 0, py: 0.5, fontSize: "0.78rem"}}>
                        <ListItemIcon sx={{minWidth: "26px !important", color: "error.main"}}><IconTrash
                            size={15}/></ListItemIcon>
                        <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>Delete</ListItemText>
                    </MenuItem>
                </Menu>

            </Stack>
        </>
    )
}